import React, { useState, useMemo, useRef } from 'react';
import { Target, Search, Clock, RefreshCw, X, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toCanvas } from 'html-to-image';
import jsPDF from 'jspdf';
import type { SavedMemo } from '../../types/geminiTypes';
import { OverviewTab } from '../Analysis/OverviewTab';
import { BusinessTab } from '../Analysis/BusinessTab';
import { ValuationTab } from '../Analysis/ValuationTab';
import { RiskTab } from '../Analysis/RiskTab';
import { MemoTab } from '../Analysis/MemoTab';
import { useDcfModel } from '../../hooks/useDcfModel';
import { getHistoricalData } from '../../services/geminiService';
import type { MarketContext } from '../../types/geminiTypes';
import type { ValueAssumptions } from '../../types/analysis';

interface MemosTabProps {
  memos: SavedMemo[];
  onRefreshMemo: (ticker: string) => void;
  onCancelRefresh: () => void;
  marketContext: MarketContext | null;
  isExporting: boolean;
  onExport: () => void;
  isLoading?: boolean;
  loadingProgress?: number;
  loadingMessage?: string;
}

export function MemosTab({ memos, onRefreshMemo, onCancelRefresh, marketContext, isExporting, onExport, isLoading, loadingProgress, loadingMessage }: MemosTabProps) {
  const [filter, setFilter] = useState<'All' | 'Buy' | 'Core Hold' | 'Pass' | 'Stale'>('All');
  const [sort, setSort] = useState<'Most recent' | 'Oldest' | 'A-Z'>('Most recent');
  const [selectedMemo, setSelectedMemo] = useState<SavedMemo | null>(null);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('overview');
  const [chartTab, setChartTab] = useState<'price' | 'comparison'>('price');
  const [useForwardMetrics, setUseForwardMetrics] = useState(true);

  const [overlayHistoricalData, setOverlayHistoricalData] = useState<any>(null);
  const [overlayComparisonData, setOverlayComparisonData] = useState<any>(null);
  const [overlayChartLoading, setOverlayChartLoading] = useState(false);
  const [overlayChartRange, setOverlayChartRange] = useState<string>('5Y');
  
  const overlayMemoRef = useRef<HTMLDivElement>(null);
  const [overlayIsExporting, setOverlayIsExporting] = useState(false);

  const getAgeDays = (runAt: any) => {
    if (!runAt) return 0;
    const date = runAt.toDate ? runAt.toDate() : new Date(runAt);
    return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  const filteredMemos = useMemo(() => {
    let result = memos;
    if (filter === 'Buy') result = result.filter(m => m.verdict.toUpperCase().includes('BUY') || m.verdict.toUpperCase().includes('ACCUMULATE') || m.verdict.toUpperCase().includes('FAT PITCH'));
    else if (filter === 'Core Hold') result = result.filter(m => m.verdict.toUpperCase().includes('HOLD') || m.verdict.toUpperCase().includes('WATCH'));
    else if (filter === 'Pass') result = result.filter(m => m.verdict.toUpperCase().includes('PASS') || m.verdict.toUpperCase().includes('AVOID'));
    else if (filter === 'Stale') result = result.filter(m => getAgeDays(m.runAt) >= 3);

    return result.sort((a, b) => {
      if (sort === 'Most recent') {
        const aTime = a.runAt?.toDate ? a.runAt.toDate().getTime() : 0;
        const bTime = b.runAt?.toDate ? b.runAt.toDate().getTime() : 0;
        return bTime - aTime;
      } else if (sort === 'Oldest') {
        const aTime = a.runAt?.toDate ? a.runAt.toDate().getTime() : 0;
        const bTime = b.runAt?.toDate ? b.runAt.toDate().getTime() : 0;
        return aTime - bTime;
      } else if (sort === 'A-Z') {
        return a.ticker.localeCompare(b.ticker);
      }
      return 0;
    });
  }, [memos, filter, sort]);

  React.useEffect(() => {
    if (selectedMemo && !isLoading) {
      const updated = memos.find(m => m.ticker === selectedMemo.ticker);
      if (updated && updated.runAt !== selectedMemo.runAt) {
        setSelectedMemo(updated);
      }
    }
  }, [memos, isLoading, selectedMemo]);

  React.useEffect(() => {
    if (!selectedMemo) {
      setOverlayHistoricalData(null);
      setOverlayComparisonData(null);
      return;
    }

    const fetchChartData = async () => {
      setOverlayChartLoading(true);
      try {
        const data = await getHistoricalData(selectedMemo.ticker, overlayChartRange);
        setOverlayHistoricalData(data);

        // Compute comparison (percent change)
        if (data.length > 0) {
          const firstValidStock = data.find(p => p.price > 0);
          const firstPrice = firstValidStock ? firstValidStock.price : 0;
          
          const firstValidSpy = data.find(p => p.spyPrice && p.spyPrice > 0);
          const firstSpyPrice = firstValidSpy ? firstValidSpy.spyPrice : 0;

          const comparison = data.map(p => ({
            ...p,
            stockGrowth: (firstPrice > 0 && p.price) ? ((p.price - firstPrice) / firstPrice) * 100 : 0,
            spyGrowth: (firstSpyPrice > 0 && p.spyPrice) ? ((p.spyPrice - firstSpyPrice) / firstSpyPrice) * 100 : 0
          }));
          setOverlayComparisonData(comparison);
        } else {
          setOverlayComparisonData([]);
        }

      } catch (err) {
        console.error("Error fetching overlay chart data:", err);
      } finally {
        setOverlayChartLoading(false);
      }
    };

    fetchChartData();
  }, [selectedMemo?.ticker, overlayChartRange]);

  const handleOverlayExportPDF = async () => {
    if (!selectedMemo || !overlayMemoRef.current) return;
    
    setOverlayIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 150)); // let react re-render isExporting classes
    try {
      const element = overlayMemoRef.current;
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pdfWidth - (margin * 2);

      const sections = Array.from(element.querySelectorAll('.pdf-section')).filter((el) => {
        const hElement = el as HTMLElement;
        return hElement.offsetHeight > 0 && hElement.offsetWidth > 0 && !hElement.parentElement?.closest('.pdf-section');
      });
      if (sections.length === 0) {
        throw new Error('No printable sections found');
      }

      let currentY = margin;
      
      // Capture canvases first to allow look-ahead
      const canvases = [];
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i] as HTMLElement;
        const keepWithNext = section.classList.contains('pdf-keep-with-next');
        const canvas = await toCanvas(section, {
          quality: 1,
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        });
        canvases.push({ canvas, keepWithNext });
      }

      for (let i = 0; i < canvases.length; i++) {
        const { canvas, keepWithNext } = canvases[i];

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

        let needsNewPage = currentY + imgHeight > pdfHeight - margin;

        if (!needsNewPage && keepWithNext && i + 1 < canvases.length) {
          const nextCanvas = canvases[i + 1].canvas;
          const nextImgHeight = (nextCanvas.height * contentWidth) / nextCanvas.width;
          if (currentY + imgHeight + 10 + nextImgHeight > pdfHeight - margin) {
            needsNewPage = true;
          }
        }

        if (needsNewPage && currentY > margin) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, imgHeight);
        currentY += imgHeight + 10;
      }

      pdf.save(`${selectedMemo.ticker}-OracleInsight-Memo.pdf`);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setOverlayIsExporting(false);
    }
  };

  const MemoOverlay = () => {
    if (!selectedMemo) return null;
    const memoAge = getAgeDays(selectedMemo.runAt);
    
    // Derived assumptions matching App.tsx logic
    const assumptions = useMemo(() => {
      const result = selectedMemo.analysisResult;
      if (result.fmpDcf?.customDcfData) {
        const custom = result.fmpDcf.customDcfData;
        return {
          revenueGrowth: Math.round(custom.revenuePercentage || (result.suggestedAssumptions?.revenueGrowth ? (result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100) : 10)),
          marginExpansion: result.suggestedAssumptions?.marginExpansion || 0,
          discountRate: Math.round(custom.wacc || (result.suggestedAssumptions?.discountRate ? (result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100) : 10)),
          terminalGrowth: Math.round(custom.longTermGrowthRate || (result.suggestedAssumptions?.terminalGrowth ? (result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100) : 3)),
          marginOfSafety: 25
        };
      } else if (result.suggestedAssumptions) {
        return {
          revenueGrowth: Math.round(result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100),
          marginExpansion: result.suggestedAssumptions.marginExpansion || 0,
          discountRate: Math.round(result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100),
          terminalGrowth: Math.round(result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100),
          marginOfSafety: 25
        };
      }
      return { revenueGrowth: 10, marginExpansion: 0, discountRate: 10, terminalGrowth: 3, marginOfSafety: 25 };
    }, [selectedMemo]);

    const dcfModel = useDcfModel(selectedMemo.analysisResult, marketContext, assumptions as unknown as ValueAssumptions);

    return (
      <div className="fixed inset-0 z-50 bg-paper/95 backdrop-blur-sm pt-safe pb-safe overflow-y-auto w-full h-full md:pb-0" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-7xl mx-auto min-h-screen bg-paper">
          <div className="sticky top-0 z-[60] bg-paper border-b border-ink-200 flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-3 md:gap-4 flex-wrap">
              <div className="px-2 py-1 bg-oxblood-50 text-oxblood font-medium rounded-md text-sm tracking-wider">
                {selectedMemo.ticker}
              </div>
              <h2 className="font-serif font-medium text-lg md:text-xl truncate max-w-[200px] md:max-w-md">{selectedMemo.companyName}</h2>
              <div className="hidden md:flex items-center gap-2">
                <span className="text-[10px] uppercase font-medium text-ink-500 tracking-wider">
                  {selectedMemo.runAt?.toDate ? selectedMemo.runAt.toDate().toLocaleString() : 'Just now'}
                </span>
                {typeof selectedMemo.analysisResult?.currentPrice === 'number' && selectedMemo.analysisResult.currentPrice > 0 && Number.isFinite(selectedMemo.analysisResult.currentPrice) && (
                  <span className="text-[10px] uppercase font-medium text-ink-500 tracking-wider">
                    <span className="font-mono">${selectedMemo.analysisResult.currentPrice.toFixed(2)}</span> <span className="lowercase">· price at analysis</span>
                  </span>
                )}
                {memoAge >= 14 ? (
                  <span className="px-2 py-0.5 bg-danger-100 text-danger-700 text-[9px] font-medium uppercase tracking-wider rounded-md">Outdated</span>
                ) : memoAge >= 3 ? (
                  <span className="px-2 py-0.5 bg-warning-100 text-warning-700 text-[9px] font-medium uppercase tracking-wider rounded-md">Stale</span>
                ) : null}
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              <button 
                onClick={() => onRefreshMemo(selectedMemo.ticker)}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-oxblood text-paper text-[10px] md:text-xs font-medium uppercase tracking-wider rounded-md hover:bg-oxblood-800 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
                <span className="hidden md:inline">{isLoading ? "Refreshing..." : "Refresh"}</span>
              </button>
              <button 
                onClick={() => {
                  if (isLoading) onCancelRefresh();
                  setSelectedMemo(null);
                }}
                className="p-1.5 md:p-2 bg-surface-2 text-ink-500 hover:text-oxblood rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[100vw] overflow-x-hidden relative">
            {isLoading && (
              <div className="flex flex-col items-center justify-center min-h-[70vh] py-20 w-full">
                <div className="w-16 h-16 border-4 border-ink-200 border-t-oxblood rounded-full animate-spin mb-6" />
                <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink-500 mb-2">Analyzing Data</div>
                <div className="text-xl md:text-2xl font-serif font-medium italic text-ink text-center px-4">
                  {loadingMessage || "Please wait..."}
                </div>
                {loadingProgress !== undefined && (
                  <div className="w-64 h-1 bg-surface-2 rounded-full overflow-hidden mt-6">
                    <div 
                      className="h-full bg-oxblood transition-all duration-300 ease-out"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            
            {!isLoading && (
              <>
                <div className="flex bg-surface-2 rounded-md p-1 gap-1 mb-8 overflow-x-auto no-scrollbar scroll-smooth snap-x border border-ink-200 w-full">
                  <button 
                    onClick={() => setActiveAnalysisTab('overview')}
                    className={cn(
                      "flex-1 text-[10px] md:text-xs font-medium uppercase tracking-widest py-3 px-4 rounded-md transition-all whitespace-nowrap snap-center",
                      activeAnalysisTab === 'overview' ? "bg-surface text-ink shadow-sm ring-1 ring-ink-200" : "text-ink-500 hover:text-ink"
                    )}
                  >
                    Overview
                  </button>
                  <button 
                    onClick={() => setActiveAnalysisTab('valuation')}
                    className={cn(
                      "flex-1 text-[10px] md:text-xs font-medium uppercase tracking-widest py-3 px-4 rounded-md transition-all whitespace-nowrap snap-center",
                      activeAnalysisTab === 'valuation' ? "bg-surface text-ink shadow-sm ring-1 ring-ink-200" : "text-ink-500 hover:text-ink"
                    )}
                  >
                    Valuation
                  </button>
                  <button 
                    onClick={() => setActiveAnalysisTab('business')}
                    className={cn(
                      "flex-1 text-[10px] md:text-xs font-medium uppercase tracking-widest py-3 px-4 rounded-md transition-all whitespace-nowrap snap-center",
                      activeAnalysisTab === 'business' ? "bg-surface text-ink shadow-sm ring-1 ring-ink-200" : "text-ink-500 hover:text-ink"
                    )}
                  >
                    Business Quality
                  </button>
                  <button 
                    onClick={() => setActiveAnalysisTab('risk')}
                    className={cn(
                      "flex-1 text-[10px] md:text-xs font-medium uppercase tracking-widest py-3 px-4 rounded-md transition-all whitespace-nowrap snap-center",
                      activeAnalysisTab === 'risk' ? "bg-surface text-ink shadow-sm ring-1 ring-ink-200" : "text-ink-500 hover:text-ink"
                    )}
                  >
                    Risk & Signals
                  </button>
                  <button 
                    onClick={() => setActiveAnalysisTab('memo')}
                    className={cn(
                      "flex-1 text-[10px] md:text-xs font-medium uppercase tracking-widest py-3 px-4 rounded-md transition-all whitespace-nowrap snap-center",
                      activeAnalysisTab === 'memo' ? "bg-oxblood text-paper shadow-sm" : "text-ink-500 hover:text-ink"
                    )}
                  >
                    Investment Memo
                  </button>
                </div>

                <div className="mt-8">
                  {activeAnalysisTab === 'overview' && <OverviewTab analysis={selectedMemo.analysisResult} marketContext={marketContext} dcfModel={dcfModel} historicalData={overlayHistoricalData} chartTab={chartTab} onChartTabChange={setChartTab} useForwardMetrics={useForwardMetrics} onToggleForwardMetrics={setUseForwardMetrics} chartRange={overlayChartRange} onChartRangeChange={setOverlayChartRange} chartLoading={overlayChartLoading} comparisonData={overlayComparisonData} />}
                  {activeAnalysisTab === 'valuation' && <ValuationTab analysis={selectedMemo.analysisResult} dcfModel={dcfModel} marketContext={marketContext as any} assumptions={assumptions as any} onAssumptionChange={() => {}} onResetAssumptions={() => {}} useForwardMetrics={false} isForward={false} />}
                  {activeAnalysisTab === 'business' && <BusinessTab analysis={selectedMemo.analysisResult} dcfModel={dcfModel} />}
                  {activeAnalysisTab === 'risk' && <RiskTab analysis={selectedMemo.analysisResult} />}
                  {activeAnalysisTab === 'memo' && <MemoTab analysis={selectedMemo.analysisResult} dcfModel={dcfModel} marketContext={marketContext} memoRef={overlayMemoRef} isExporting={overlayIsExporting} onExport={handleOverlayExportPDF} />}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-ink-200 pb-6 pt-4">
        <div>
          <h1 className="text-[2rem] font-serif font-medium italic tracking-tight text-ink leading-none mb-2">My Memos</h1>
          <p className="text-ink-500 font-mono text-xs max-w-md leading-relaxed uppercase tracking-widest">
            Saved analysis history securely stored.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {['All', 'Buy', 'Core Hold', 'Pass', 'Stale'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={cn(
                "px-3 py-1.5 rounded-md text-[10px] font-medium uppercase tracking-wider border transition-all",
                filter === f ? "bg-oxblood text-paper border-oxblood" : "bg-surface text-ink-500 border-ink-200 hover:border-oxblood/30"
              )}
            >
              {f}
            </button>
          ))}
        </div>
        
        <div className="relative group">
          <select 
            value={sort}
            onChange={e => setSort(e.target.value as any)}
            className="appearance-none pl-4 pr-10 py-2 bg-surface border border-ink-200 rounded-md text-xs font-medium uppercase tracking-wider text-ink-600 focus:outline-none focus:ring-2 focus:ring-oxblood/20"
          >
            <option value="Most recent">Most Recent</option>
            <option value="Oldest">Oldest</option>
            <option value="A-Z">A-Z</option>
          </select>
          <ChevronDown className="w-4 h-4 text-ink-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {memos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
          <div className="w-16 h-16 mb-6 rounded-md bg-surface-2 border border-ink-200 flex items-center justify-center">
            <Target className="w-6 h-6 text-ink-300" />
          </div>
          <h3 className="text-xl font-serif font-medium text-ink mb-2">No saved analyses yet</h3>
          <p className="text-xs text-ink-500 font-mono">Run an analysis in the Analyzer to save your first memo.</p>
        </div>
      ) : filteredMemos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <p className="text-xs text-ink-500 font-mono">No memos match the selected filter.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredMemos.map(memo => {
            const age = getAgeDays(memo.runAt);
            const isBuy = memo.verdict.toUpperCase().includes('BUY') || memo.verdict.toUpperCase().includes('ACCUMULATE') || memo.verdict.toUpperCase().includes('FAT PITCH');
            const isCoreHold = memo.verdict.toUpperCase().includes('HOLD') || memo.verdict.toUpperCase().includes('WATCH');
            const isPass = memo.verdict.toUpperCase().includes('PASS') || memo.verdict.toUpperCase().includes('AVOID');
            
            return (
              <div 
                key={memo.ticker}
                onClick={() => setSelectedMemo(memo)}
                className="group flex flex-col sm:flex-row sm:items-center justify-between oi-card p-4 hover:border-oxblood/30 cursor-pointer transition-all duration-300 gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="px-3 py-1.5 bg-oxblood-50 text-oxblood font-medium rounded-md text-sm tracking-wider border border-oxblood/20 shrink-0">
                    {memo.ticker}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <h3 className="font-serif font-medium text-lg text-ink truncate pr-4">{memo.companyName}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3 text-ink-500" />
                      <span className="text-[10px] text-ink-500 font-mono">
                        {memo.runAt?.toDate ? memo.runAt.toDate().toLocaleDateString() : 'Just now'}
                      </span>
                      {age >= 14 ? (
                        <span className="px-1.5 py-0.5 bg-danger-100 text-danger-700 text-[8px] font-medium uppercase tracking-wider rounded-md">Outdated</span>
                      ) : age >= 3 ? (
                        <span className="px-1.5 py-0.5 bg-warning-100 text-warning-700 text-[8px] font-medium uppercase tracking-wider rounded-md">Stale</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-auto w-full pt-3 sm:pt-0 border-t sm:border-0 border-ink-200 mt-1 sm:mt-0">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center sm:items-end">
                      <span className="text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500 mb-1">IV</span>
                      <span className="text-sm font-mono font-medium text-ink">
                        ${((memo.analysisResult as any)?.scenarios?.base?.intrinsicValuePerShare) > 0 ? ((memo.analysisResult as any).scenarios.base.intrinsicValuePerShare).toFixed(2) : memo.intrinsicValue > 0 ? memo.intrinsicValue.toFixed(2) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className={cn(
                      "px-3 py-1 text-[10px] font-medium uppercase tracking-widest rounded-lg border",
                      isBuy ? "bg-success-50 text-success-700 border-success-100" :
                      isCoreHold ? "bg-warning-50 text-warning-700 border-warning-100" :
                      isPass ? "bg-danger-50 text-danger-700 border-danger-100" :
                      "bg-surface-2 text-ink-600 border-ink-200"
                    )}>
                      {memo.verdict}
                    </div>
                  </div>
                  
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefreshMemo(memo.ticker);
                    }}
                    className="flex shrink-0 items-center gap-1.5 px-3 py-1.5 bg-surface-2 hover:bg-oxblood hover:text-paper text-ink-600 rounded-md transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <MemoOverlay />
    </div>
  );
}
